import { Component, OnInit, OnDestroy, ViewChildren, QueryList, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AddChartComponent } from '../add-chart/add-chart.component';
import { ChartOverviewComponent } from '../chart-overview/chart-overview.component';
import { UserDashboardService, ChartData, SaveDashboardRequest, extractChartAuditFields } from '../../../services/user-dashboard.service';
import { chartQueryFieldsFromApi } from '../../../shared/chart-insights-drilldown.util';
import { normalizeHighchartsApiConfig } from '../../../shared/chart-label.util';
import { NotificationService } from '../../../services/notification.service';
import { DashboardNavigationService } from '../../../services/dashboard-navigation.service';
import { ConfirmPopupService } from '../../../services/confirm-popup.service';
import { PopupConfirmationData } from '../../../shared/constant';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, firstValueFrom, Subscription } from 'rxjs';
import { CanComponentDeactivate } from '../../../interfaces/can-component-deactivate.interface';
import { BetaService } from '../../../services/beta.service';

@Component({
  selector: 'create-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, AddChartComponent, ChartOverviewComponent, MatDialogModule, MatTooltipModule],
  templateUrl: './create-dashboard.component.html',
  styleUrls: ['./create-dashboard.component.scss'],
})
export class CreateDashboardComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  dashboardName: string = 'Untitled Dashboard';
  charts: ChartData[] = [];
  @ViewChildren(ChartOverviewComponent) chartOverviewComponents!: QueryList<ChartOverviewComponent>;
  selectedLayout: string = '2-column';
  showAddChartPanel: boolean = false;
  showChartForm: boolean = false;
  selectedChartSlot: number | null = null;
  dashboardId: string | null = null;
  allDashboards: any[] = [];
  isLoadingDashboards: boolean = false;
  showLayoutDropdown: boolean = false;
  preselectedChartTypeId: string | null = null; // Chart type ID to pre-select in form
  isEditingTitle: boolean = false; // Track if dashboard title is being edited
  isViewMode: boolean = false; // Track if in view mode (for existing dashboards)
  _isEditMode: boolean = false; // Track if in edit mode (internal state)
  isLoadingDashboard: boolean = false; // Track dashboard loading state
  isSyncing: boolean = false; // Track dashboard chart sync state
  showDashboardDropdown: boolean = false; // Track dropdown open/close state
  dashboardSearchQuery: string = ''; // Search query for dashboard dropdown
  showMoreDropdown: boolean = false; // Track more dropdown open/close state
  isSharedWithAll: boolean = false; // Track if dashboard is shared with all users
  hasUnsavedChanges: boolean = false; // Track if there are unsaved changes
  originalDashboardData: any = null; // Store original data for comparison
  dashboardOwnerId: string | null = null; // Store dashboard owner ID for authorization checks
  private syncSubscription?: Subscription;
  private autoSyncedDashboardId: string | null = null;

  layouts = [
    { id: '1-column', name: '1 Column', columns: 1 },
    { id: '2-column', name: '2 Columns', columns: 2 },
  ];


  constructor(
    private userDashboardService: UserDashboardService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private dashboardNavigationService: DashboardNavigationService,
    private confirmPopupService: ConfirmPopupService,
    private betaService: BetaService
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        const openedDashboardId = params['id'];
        if (this.dashboardId !== openedDashboardId) {
          this.cancelInFlightSync();
          this.autoSyncedDashboardId = null;
        }
        this.dashboardId = openedDashboardId;
        // When opening existing dashboard, start in view mode
        this.isViewMode = true;
        this._isEditMode = false;
        // Notify navigation service about view mode
        this.dashboardNavigationService.setEditMode(false);
        // Load all dashboards for the dropdown selector
        this.loadAllDashboards();
        this.loadDashboard(openedDashboardId, false, () => {
          this.autoSyncOpenedDashboard(openedDashboardId);
        });
      } else {
        this.cancelInFlightSync();
        this.autoSyncedDashboardId = null;
        // New dashboard - start in edit mode
        this.isViewMode = false;
        this._isEditMode = true;
        // Notify navigation service about edit mode
        this.dashboardNavigationService.setEditMode(true);
        // Load draft from localStorage for new dashboard
        this.loadDraftFromLocalStorage();
        this.storeOriginalData(); // Store original data after loading draft
        this.checkForUnsavedChanges(); // Check initial state
      }
    });

    // Listen for back button clicks from header
    this.dashboardNavigationService.backButtonClick$.subscribe(() => {
      if (this.showChartForm) {
        this.handleCloseAddChartPanel();
      } else if (this._isEditMode && this.dashboardId) {
        // If in edit mode with existing dashboard, go back to view mode
        this.exitEditMode();
      } else if (this._isEditMode && !this.dashboardId) {
        // If in edit mode with new dashboard, navigate back to user-dashboard
        // This will trigger the canDeactivate guard
        this.router.navigate(['user-dashboard']);
      } else if (this.isViewMode && this.dashboardId) {
        // If in view mode, navigate back to user-dashboard
        this.router.navigate(['user-dashboard']);
      }
    });

    // Update chart form state in service
    this.updateChartFormState();
  }

  loadAllDashboards() {
    this.isLoadingDashboards = true;
    this.userDashboardService.getAllDashboards().subscribe({
      next: (response: any) => {
        // Handle different response formats
        let dashboardsArray: any[] = [];

        if (Array.isArray(response)) {
          dashboardsArray = response;
        } else if (response && Array.isArray(response.data)) {
          dashboardsArray = response.data;
        } else if (response && response.dashboards && Array.isArray(response.dashboards)) {
          dashboardsArray = response.dashboards;
        } else if (response && response.data && Array.isArray(response.data.dashboards)) {
          dashboardsArray = response.data.dashboards;
        }

        // Map API response (API uses PascalCase: DashboardId, DashboardName)
        this.allDashboards = dashboardsArray.map((dashboard: any) => ({
          id: dashboard.DashboardId || dashboard.dashboard_id || dashboard.id,
          name: dashboard.DashboardName || dashboard.dashboard_name || dashboard.name
        }));
        this.isLoadingDashboards = false;
      },
      error: (error: any) => {
        console.error('Error loading dashboards:', error);
        this.allDashboards = [];
        this.isLoadingDashboards = false;
      }
    });
  }

  onDashboardSwitch(dashboardId: string) {
    if (dashboardId && dashboardId !== this.dashboardId) {
      // Navigate to the selected dashboard
      this.router.navigate(['create-dashboard'], {
        queryParams: { id: dashboardId }
      });
      this.showDashboardDropdown = false;
      this.dashboardSearchQuery = '';
    }
  }

  toggleDashboardDropdown() {
    this.showDashboardDropdown = !this.showDashboardDropdown;
    if (!this.showDashboardDropdown) {
      this.dashboardSearchQuery = '';
    }
  }

  getFilteredDashboards() {
    if (!this.dashboardSearchQuery.trim()) {
      return this.allDashboards;
    }
    const query = this.dashboardSearchQuery.toLowerCase();
    return this.allDashboards.filter(dashboard =>
      dashboard.name.toLowerCase().includes(query)
    );
  }

  getSelectedDashboardName(): string {
    if (!this.dashboardId) {
      return 'Select Dashboard';
    }
    const selected = this.allDashboards.find(d => d.id === this.dashboardId);
    return selected ? selected.name : 'Select Dashboard';
  }

  handleViewAllDashboards() {
    this.showDashboardDropdown = false;
    this.dashboardSearchQuery = '';
    this.router.navigate(['user-dashboard']);
  }


  loadDraftFromLocalStorage() {
    const draft = this.userDashboardService.getDraftFromLocalStorage();
    if (draft) {
      this.dashboardName = draft.dashboardName;
      this.charts = draft.charts || [];
      this.selectedLayout = draft.layout || '2-column';
      if (draft.dashboardId) {
        this.dashboardId = draft.dashboardId;
      }
    }
  }

  saveToLocalStorage() {
    this.userDashboardService.saveDraftToLocalStorage(
      this.dashboardName,
      this.charts,
      this.selectedLayout,
      this.dashboardId || undefined
    );
  }

  loadDashboard(id: string, silent: boolean = false, onComplete?: () => void) {
    if (!silent) {
      this.isLoadingDashboard = true;
    }
    this.userDashboardService.getDashboardById(id).subscribe({
      next: (response: any) => {
        // Handle different response formats
        let dashboardData: any;
        if (response.data) {
          dashboardData = response.data;
        } else {
          dashboardData = response;
        }

        // Map API response (API uses PascalCase: DashboardId, DashboardName)
        // Map API response (API uses PascalCase: DashboardId, DashboardName, Layout, IsSharedWithAll)
        this.dashboardId = dashboardData.DashboardId || dashboardData.dashboard_id || dashboardData.id || id;
        this.dashboardName = dashboardData.DashboardName || dashboardData.dashboard_name || dashboardData.name || 'Untitled Dashboard';
        this.selectedLayout = dashboardData.Layout || dashboardData.layout || '2-column';
        this.isSharedWithAll = dashboardData.IsSharedWithAll || dashboardData.is_shared_with_all || false;
        // Store dashboard owner ID for authorization checks
        this.dashboardOwnerId = dashboardData.OwnerId || dashboardData.owner_id || dashboardData.generated_by || null;

        // Load charts from response and normalize data
        // API returns PascalCase: Charts (capital C), but also check for lowercase for compatibility
        const chartsArray = dashboardData.Charts || dashboardData.charts;
        if (chartsArray && Array.isArray(chartsArray)) {
          this.charts = chartsArray.map((chart: any) => {
            // Helper function to clean categories (remove null/undefined values)
            const cleanCategories = (cats: any[]): string[] => {
              if (!Array.isArray(cats)) {
                return [];
              }
              return cats
                .filter((cat: any) => cat !== null && cat !== undefined && cat !== '')
                .map((cat: any) => {
                  if (typeof cat === 'string') {
                    return cat.trim();
                  }
                  return String(cat || 'Unknown').trim();
                })
                .filter((cat: string) => cat.length > 0);
            };

            // API returns PascalCase format: Series, Categories, ChartConfig, ChartId, ChartType, etc.
            // Map them to the expected structure for chart-overview component
            const rawCategories = chart.Categories || [];
            const chartDataObj: any = {
              // Extract series and categories from API (PascalCase)
              series: chart.Series || [],
              categories: cleanCategories(rawCategories), // Clean categories to remove nulls
              table_data: chart.TableData || [],
              // Preserve other fields
              description: chart.Description || {},
              metric_type: chart.MetricType,
              // Extract axis labels from ChartConfig (nested objects use camelCase: xAxis, yAxis)
              xAxisLabel: chart.ChartConfig?.xAxis?.title?.text || '',
              yAxisLabel: chart.ChartConfig?.yAxis?.title?.text || ''
            };

            // Normalize chart data - convert string numeric values to numbers
            const normalizedData = this.normalizeChartData(chartDataObj);

            // Extract and normalize config - API uses PascalCase: ChartConfig
            const rawConfig = chart.ChartConfig || {};
            const normalizedConfig = this.normalizeChartConfig(rawConfig);

            // Preserve table_data
            const tableData = chart.TableData || normalizedData.table_data;

            // Extract title from API (PascalCase: Title)
            const chartTitle = chart.Title
              || chart.ChartConfig?.title?.text
              || 'Untitled Chart';

            return {
              id: chart.ChartId || chart.id,
              title: chartTitle,
              chart_type: chart.ChartType,
              data: {
                ...normalizedData,
                table_data: tableData // Preserve table_data in the data object
              },
              config: normalizedConfig,
              table_data: tableData, // Also preserve at top level for easy access
              ...extractChartAuditFields(chart),
              ...chartQueryFieldsFromApi(chart),
              DurationBehavior: chart.DurationBehavior,
              no_data_found: chart.NoDataFound ?? false
            };
          });
        } else {
          this.charts = [];
        }

        // Save to localStorage for auto-save (only in edit mode)
        if (this._isEditMode) {
          this.saveToLocalStorage();
          this.storeOriginalData(); // Store original data after loading
        }
        this.isLoadingDashboard = false;
        onComplete?.();
      },
      error: (error: any) => {
        console.error('Error loading dashboard:', error);
        // If loading fails, try loading from localStorage
        this.loadDraftFromLocalStorage();
        this.isLoadingDashboard = false;
        onComplete?.();
      }
    });
  }

  handleBackClick() {
    // If creating chart, go back to dashboard view first
    if (this.showChartForm) {
      this.handleCloseAddChartPanel();
    } else {
      // Use the same logic as handleCancel for consistency
      this.handleCancel();
    }
  }

  handleEditClick() {
    // Switch from view mode to edit mode
    this._isEditMode = true;
    this.isViewMode = false;
    // Notify navigation service about edit mode
    this.dashboardNavigationService.setEditMode(true);
    // Store original data when entering edit mode for existing dashboard
    this.storeOriginalData();
    // Check initial state when entering edit mode
    this.checkForUnsavedChanges();
  }

  exitEditMode() {
    // Check for unsaved changes before exiting edit mode
    this.checkForUnsavedChanges();

    if (this.hasUnsavedChanges) {
      // Show warning popup if there are unsaved changes
      firstValueFrom(this.confirmPopupService.openConfirmDialog({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes to your dashboard. Are you sure you want to exit edit mode without saving?',
        confirmText: 'Exit',
        cancelText: 'Stay',
        confirmButtonClass: 'leave-button'
      })).then((result: boolean) => {
        if (result === true) {
          // User confirmed, proceed with exiting edit mode
          this.performExitEditMode();
        }
        // If user cancelled, do nothing (stay in edit mode)
      });
    } else {
      // No unsaved changes, proceed directly
      this.performExitEditMode();
    }
  }

  private performExitEditMode() {
    // Actual exit edit mode logic (without checking for unsaved changes)
    this._isEditMode = false;
    this.isViewMode = true;
    // Reset unsaved changes flag when exiting edit mode without saving
    this.hasUnsavedChanges = false;
    // Notify navigation service about exiting edit mode
    this.dashboardNavigationService.setEditMode(false);
    // Load all dashboards for the dropdown selector
    this.loadAllDashboards();
    // Reload dashboard to get latest data
    if (this.dashboardId) {
      this.loadDashboard(this.dashboardId);
    }
  }

  handleRefresh() {
    this.handleSync();
  }

  handleSync(options: { silent?: boolean } = {}) {
    if (!this.dashboardId || this.isSyncing) {
      return;
    }

    const dashboardId = this.dashboardId;
    this.isSyncing = true;
    this.syncSubscription = this.userDashboardService.syncDashboardCharts(dashboardId).subscribe({
      next: (response: any) => {
        if (this.dashboardId !== dashboardId) {
          this.isSyncing = false;
          this.syncSubscription = undefined;
          return;
        }

        const data = response?.data || response;
        const synced = data?.synced ?? 0;
        const failed = Array.isArray(data?.failed) ? data.failed : [];

        this.loadDashboard(dashboardId, true, () => {
          this.isSyncing = false;
          this.syncSubscription = undefined;
          this.notifySyncResult(synced, failed, options.silent);
        });
      },
      error: (error: any) => {
        this.isSyncing = false;
        this.syncSubscription = undefined;
        if (this.dashboardId !== dashboardId) {
          return;
        }
        const detail = error?.error?.detail || error?.error?.message || error?.message;
        const message = typeof detail === 'string' && detail.trim()
          ? detail
          : 'Could not sync charts. Please try again.';
        this.notificationService.error('Sync failed', message);
      }
    });
  }

  private autoSyncOpenedDashboard(dashboardId: string) {
    if (!dashboardId || this.dashboardId !== dashboardId || this.autoSyncedDashboardId === dashboardId) {
      return;
    }
    this.autoSyncedDashboardId = dashboardId;
    this.handleSync({ silent: true });
  }

  private cancelInFlightSync() {
    this.syncSubscription?.unsubscribe();
    this.syncSubscription = undefined;
    this.isSyncing = false;
  }

  private notifySyncResult(synced: number, failed: any[], silent?: boolean) {
    if (failed.length > 0 && synced === 0) {
      this.notificationService.error('Sync failed', 'Could not refresh charts. Please try again.');
    } else if (failed.length > 0) {
      this.notificationService.info(
        'Partially synced',
        `${synced} chart(s) updated. ${failed.length} could not be refreshed.`
      );
    } else if (!silent) {
      this.notificationService.success(
        'Synced',
        synced ? `${synced} chart(s) refreshed with current data.` : 'No charts to sync.'
      );
    }
  }

  handleCancel() {
    if (this.dashboardId && this._isEditMode) {
      // If editing existing dashboard, exit edit mode (which will check for unsaved changes)
      this.exitEditMode();
    } else {
      // For new dashboards, navigate back to dashboard list
      // This will trigger the canDeactivate guard to check for unsaved changes
      this.router.navigate(['user-dashboard']);
    }
  }

  handleSave() {
    // Validate dashboard name
    if (!this.dashboardName || this.dashboardName.trim() === '') {
      this.notificationService.error('Error', 'Enter dashboard name');
      return;
    }

    if (this.dashboardName.trim() === 'Untitled Dashboard') {
      this.notificationService.error('Error', 'Dashboard title is required');
      return;
    }

    // Extract chart IDs from charts array
    const chartIds = this.charts
      .filter(chart => chart.id)
      .map(chart => chart.id!);

    if (chartIds.length === 0) {
      this.notificationService.error('Error', 'Add at least one chart');
      return;
    }

    const request: SaveDashboardRequest = {
      dashboard_name: this.dashboardName.trim(),
      chart_ids: chartIds,
      layout: this.selectedLayout || '2-column',
      status: 'active'
    };

    // Show loading state
    const saveButton = document.querySelector('.btn-save') as HTMLButtonElement;
    const originalText = saveButton?.textContent;
    if (saveButton) {
      saveButton.disabled = true;
      saveButton.textContent = 'Saving...';
    }

    const saveObservable = this.dashboardId
      ? this.userDashboardService.updateDashboard(this.dashboardId, request)
      : this.userDashboardService.saveDashboard(request);

    saveObservable.subscribe({
      next: (response: any) => {
        // Extract dashboard_id from response (API uses PascalCase: DashboardId)
        let savedDashboardId: string;
        if (response.data && response.data.DashboardId) {
          savedDashboardId = response.data.DashboardId;
        } else if (response.data && response.data.dashboard_id) {
          savedDashboardId = response.data.dashboard_id;
        } else if (response.DashboardId) {
          savedDashboardId = response.DashboardId;
        } else if (response.dashboard_id) {
          savedDashboardId = response.dashboard_id;
        } else if (response.id) {
          savedDashboardId = response.id;
        } else {
          savedDashboardId = this.dashboardId || '';
        }

        // Update dashboardId if it was a new dashboard
        if (!this.dashboardId) {
          this.dashboardId = savedDashboardId;
        }

        // Clear localStorage draft after successful save
        this.userDashboardService.clearDraftFromLocalStorage();

        // Reset unsaved changes flag after successful save
        this.hasUnsavedChanges = false;
        this.storeOriginalData(); // Update original data to current state

        // Restore button state
        if (saveButton) {
          saveButton.disabled = false;
          saveButton.textContent = originalText || 'Save';
        }

        // Show success notification
        const action = this.dashboardId ? 'Updated' : 'Saved';
        this.notificationService.success(action, 'Dashboard saved.');

        // Navigate back to dashboard list
        this.router.navigate(['user-dashboard']);
      },
      error: (error: any) => {
        console.error('Error saving dashboard:', error);

        let errorTitle = 'Error';
        let errorMessage = 'Save failed';

        if (error?.error?.detail) {
          if (typeof error.error.detail === 'string') {
            errorMessage = error.error.detail.length > 50
              ? 'Validation error. Check your input'
              : error.error.detail;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          }
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.status === 0 || error?.status === 500) {
          errorMessage = 'Server connection failed';
        } else if (error?.status === 400) {
          errorMessage = 'Invalid request';
        }

        this.notificationService.error(errorTitle, errorMessage);

        // Restore button state
        if (saveButton) {
          saveButton.disabled = false;
          saveButton.textContent = originalText || 'Save';
        }
      }
    });
  }

  toggleLayoutDropdown() {
    this.showLayoutDropdown = !this.showLayoutDropdown;
  }

  handleChangeLayout(layoutId?: string) {
    if (layoutId) {
      this.selectedLayout = layoutId;
      this.showLayoutDropdown = false;
      this.saveToLocalStorage();
      this.checkForUnsavedChanges(); // Check for unsaved changes
    } else {
      // Toggle dropdown if no layout selected
      this.toggleLayoutDropdown();
    }
  }



  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;

    // Handle layout dropdown
    if (!target.closest('.layout-dropdown-container')) {
      this.showLayoutDropdown = false;
    }

    // Handle dashboard selector dropdown
    if (!target.closest('.dashboard-selector-wrapper')) {
      this.showDashboardDropdown = false;
      this.dashboardSearchQuery = '';
    }

    // Handle more dropdown
    if (!target.closest('.more-dropdown-container')) {
      this.showMoreDropdown = false;
    }

  }

  handleAddChart() {
    this.showAddChartPanel = true;
    this.showChartForm = false; // Don't show form until template is selected
    this.disableBodyScroll();
  }

  handleCloseAddChartPanel() {
    this.showAddChartPanel = false;
    this.showChartForm = false;
    this.selectedChartSlot = null;
    this.preselectedChartTypeId = null; // Reset preselected chart type
    this.enableBodyScroll();
    this.updateChartFormState();
  }

  private updateChartFormState() {
    this.dashboardNavigationService.setChartFormOpen(this.showChartForm);
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: Event) {
    if ((this.showChartForm || this.showAddChartPanel) && event instanceof KeyboardEvent) {
      this.handleCloseAddChartPanel();
    }
  }

  private disableBodyScroll() {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  }

  private enableBodyScroll() {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  ngOnDestroy() {
    this.enableBodyScroll();
    this.cancelInFlightSync();
    // Reset edit mode when component is destroyed
    this.dashboardNavigationService.setEditMode(false);
  }

  handleChartAdded(chart: ChartData) {
    if (this.selectedChartSlot !== null) {
      this.charts[this.selectedChartSlot] = chart;
    } else {
      this.charts.push(chart);
    }
    this.showChartForm = false;
    this.showAddChartPanel = false;
    this.selectedChartSlot = null;
    this.preselectedChartTypeId = null; // Reset preselected chart type

    // Save to localStorage after adding chart
    this.saveToLocalStorage();
    this.checkForUnsavedChanges(); // Check for unsaved changes
  }

  /** When chart generate/update API fails (non-200), remove that chart card from the UI. */
  handleChartUpdateFailed(chartId: string) {
    const index = this.charts.findIndex((c) => c.id === chartId);
    if (index !== -1) {
      this.charts.splice(index, 1);
      this.saveToLocalStorage();
      this.checkForUnsavedChanges();
    }
    this.showChartForm = false;
    this.showAddChartPanel = false;
    this.selectedChartSlot = null; // editingChart / editingChartId getters will return null
  }

  onDashboardNameChange() {
    // Save to localStorage when dashboard name changes
    this.saveToLocalStorage();
    this.checkForUnsavedChanges(); // Check for unsaved changes
  }

  enableTitleEdit() {
    this.isEditingTitle = true;
  }

  disableTitleEdit() {
    this.isEditingTitle = false;
  }

  handleChartSlotClick(index: number) {
    // Only handle empty slot clicks - don't open edit form on chart click
    // Edit form only opens via Edit button in the chart menu
    if (!this.charts[index]) {
      // Empty slot - open templates panel
      this.selectedChartSlot = index;
      this.showAddChartPanel = true;
      this.showChartForm = false; // Don't show form on slot click
    }
  }

  handleRemoveChart(index: number, event: Event) {
    event.stopPropagation(); // Prevent triggering slot click
    this.confirmPopupService.openConfirmDialog(PopupConfirmationData.deleteChartConfirmation).subscribe((result) => {
      if (result === true) {
        // Remove chart at index
        this.charts.splice(index, 1);
        // Save to localStorage after removing chart
        this.saveToLocalStorage();
        this.checkForUnsavedChanges(); // Check for unsaved changes
      }
    });
  }

  handleRemoveChartFromMenu(index: number) {
    // Only allow deletion in edit mode
    if (!this.isEditMode) {
      return;
    }
    // Handle chart removal from menu (no event needed)
    this.confirmPopupService.openConfirmDialog(PopupConfirmationData.deleteChartConfirmation).subscribe((result) => {
      if (result === true) {
        // Remove chart at index
        this.charts.splice(index, 1);
        // Save to localStorage after removing chart
        this.saveToLocalStorage();
        this.checkForUnsavedChanges(); // Check for unsaved changes
      }
    });
  }

  handleChartSelected(chartTypeId: string) {
    // Store the chart type ID to pre-select in the form
    this.preselectedChartTypeId = chartTypeId;
    // Only show form when a chart template is actually selected
    this.showChartForm = true;
    this.showAddChartPanel = false; // Hide templates panel when form is shown
    this.disableBodyScroll();
    this.updateChartFormState();
  }

  editChart(index: number) {
    // Only allow editing in edit mode
    if (!this.isEditMode) {
      return;
    }
    // Set the selected slot and show chart form for editing
    this.selectedChartSlot = index;
    this.showChartForm = true;
    this.showAddChartPanel = false;
    this.preselectedChartTypeId = null; // Reset preselected chart type when editing
    this.disableBodyScroll();
    this.updateChartFormState();
  }

  handleDownloadChart(index: number) {
  }

  handleSaveAndLargeView(index: number) {
    if (!this.canSave()) {
      if (!this.dashboardName?.trim()) {
        this.notificationService.error('Error', 'Enter dashboard name');
      } else if (this.dashboardName.trim() === 'Untitled Dashboard') {
        this.notificationService.error('Error', 'Dashboard title is required');
      } else {
        this.notificationService.error('Error', 'Add at least one chart');
      }
      return;
    }

    // Create a promise wrapper around the save operation
    const savePromise = new Promise<void>((resolve, reject) => {
      // Store the original success handler
      const originalNext = this.handleSave.bind(this);

      // Override the save operation temporarily
      const chartIds = this.charts.map((chart: ChartData) => chart.id).filter((id): id is string => id !== undefined);
      const request: SaveDashboardRequest = {
        dashboard_name: this.dashboardName.trim(),
        chart_ids: chartIds,
        layout: this.selectedLayout || '2-column'
      };

      const saveButton = document.querySelector('.btn-save') as HTMLButtonElement;
      if (saveButton) {
        saveButton.textContent = 'Saving...';
        saveButton.disabled = true;
      }

      const saveObservable = this.dashboardId
        ? this.userDashboardService.updateDashboard(this.dashboardId, request)
        : this.userDashboardService.saveDashboard(request);

      saveObservable.subscribe({
        next: (response: any) => {
          // Extract dashboard_id from response (API uses PascalCase: DashboardId)
          let savedDashboardId: string;
          if (response.data && response.data.DashboardId) {
            savedDashboardId = response.data.DashboardId;
          } else if (response.data && response.data.dashboard_id) {
            savedDashboardId = response.data.dashboard_id;
          } else if (response.DashboardId) {
            savedDashboardId = response.DashboardId;
          } else if (response.dashboard_id) {
            savedDashboardId = response.dashboard_id;
          } else if (response.id) {
            savedDashboardId = response.id;
          } else {
            savedDashboardId = this.dashboardId || '';
          }

          // Update dashboardId if it was a new dashboard
          if (!this.dashboardId) {
            this.dashboardId = savedDashboardId;
          }

          // Clear localStorage draft after successful save
          this.userDashboardService.clearDraftFromLocalStorage();

          // Reset unsaved changes flag after successful save
          this.hasUnsavedChanges = false;
          this.storeOriginalData(); // Update original data to current state

          // Restore button state
          if (saveButton) {
            saveButton.textContent = 'Save';
            saveButton.disabled = false;
          }

          // Show success notification
          this.notificationService.success('Dashboard Saved', 'Dashboard saved successfully!');

          // Resolve the promise
          resolve();
        },
        error: (error: any) => {
          console.error('Error saving dashboard:', error);

          // Restore button state
          if (saveButton) {
            saveButton.textContent = 'Save';
            saveButton.disabled = false;
          }

          // Show error notification
          const errorMessage = error?.error?.detail || error?.error?.message || 'Failed to save dashboard';
          this.notificationService.error('Save Failed', errorMessage);

          // Reject the promise
          reject(error);
        }
      });
    });

    // Execute save and then navigate to large view
    savePromise.then(() => {
      // After successful save, get the chart component and trigger large view
      const chartComponents = this.chartOverviewComponents?.toArray();
      if (chartComponents && chartComponents[index]) {
        chartComponents[index].navigateToLargeView();
      }
    }).catch((error) => {
      console.error('Error in save and large view flow:', error);
    });
  }

  handleShareChart(index: number) {
  }

  get isEditMode(): boolean {
    // For new dashboards (no dashboardId), always in edit mode
    if (!this.dashboardId) {
      return this._isEditMode;
    }
    // For existing dashboards, use the internal edit mode state
    return this._isEditMode;
  }

  get editingChartIndex(): number | null {
    return this.selectedChartSlot;
  }

  get editingChart(): ChartData | null {
    if (this.selectedChartSlot !== null && this.charts[this.selectedChartSlot]) {
      return this.charts[this.selectedChartSlot];
    }
    return null;
  }

  get editingChartId(): string | null {
    if (this.selectedChartSlot !== null && this.charts[this.selectedChartSlot]?.id) {
      return this.charts[this.selectedChartSlot].id || null;
    }
    return null;
  }

  getLayoutColumns(): number {
    const layout = this.layouts.find(l => l.id === this.selectedLayout);
    return layout?.columns || 2;
  }

  getChartSlots(): number[] {
    const columns = this.getLayoutColumns();
    const chartCount = this.charts.length;

    // If no charts, show only 1 empty slot for adding the first chart
    if (chartCount === 0) {
      return [0];
    }

    // Show exactly the number of slots needed for all charts
    // No extra empty slots - users can add charts via "Add Chart" button
    return Array.from({ length: chartCount }, (_, i) => i);
  }

  canSave(): boolean {
    // Enable save button only if:
    // 1. Dashboard name is not empty
    // 2. At least one chart exists
    return this.dashboardName.trim() !== '' &&
           this.dashboardName.trim() !== 'Untitled Dashboard' &&
           this.charts.length > 0;
  }

  toggleMoreDropdown() {
    this.showMoreDropdown = !this.showMoreDropdown;
  }

  // Check if current user is the dashboard creator/owner
  isDashboardCreator(): boolean {
    const currentUserId = this.betaService.getCurrentUserId();
    if (!currentUserId || !this.dashboardOwnerId) {
      return false;
    }
    return currentUserId === this.dashboardOwnerId;
  }

  // Show unauthorized alert for non-creators - Method removed as popups are disabled
  // showUnauthorizedAlert(action: string) { ... }

  handleSetDefaultView() {
    this.showMoreDropdown = false;
    this.notificationService.success('Coming Soon', 'Feature not available yet');
  }

  handleShareDashboard() {
    if (!this.dashboardId) {
      this.notificationService.error('Error', 'Please save the dashboard first.');
      this.showMoreDropdown = false;
      return;
    }

    this.showMoreDropdown = false;

    // If already shared, unshare it
    if (this.isSharedWithAll) {
      this.userDashboardService.unshareDashboard(this.dashboardId).subscribe({
        next: () => {
          // Removed unshare success popup
          this.isSharedWithAll = false;
          if (this.dashboardId) {
            this.loadDashboard(this.dashboardId);
          }
        },
        error: (error: any) => {
          console.error('Error unsharing dashboard:', error);
          // Handle 403 Forbidden error
          if (error?.status === 403) {
            this.notificationService.info('Access Denied', 'Insufficient permissions');
          } else {
            this.notificationService.error('Error', 'Failed to unshare dashboard');
          }
        }
      });
    } else {
      // Share with all users
      this.userDashboardService.shareDashboard(this.dashboardId).subscribe({
        next: () => {
          // Removed share success popup
          this.isSharedWithAll = true;
          if (this.dashboardId) {
            this.loadDashboard(this.dashboardId);
          }
        },
        error: (error: any) => {
          console.error('Error sharing dashboard:', error);
          // Handle 403 Forbidden error
          if (error?.status === 403) {
            this.notificationService.info('Access Denied', 'Insufficient permissions');
          } else {
            this.notificationService.error('Error', 'Failed to share dashboard');
          }
        }
      });
    }
  }

  getShareButtonText(): string {
    return this.isSharedWithAll ? 'Unshare Dashboard' : 'Share Dashboard';
  }

  handleViewAllDashboardsFromMore() {
    this.showMoreDropdown = false;
    this.router.navigate(['user-dashboard']);
  }

  normalizeChartData(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const normalized: any = {
      ...data
    };

    // Normalize series data - convert string numeric values to numbers; time series [dateStr, value] -> [timestampMs, value]
    if (Array.isArray(data.series)) {
      normalized.series = data.series.map((series: any) => {
        if (!series || !Array.isArray(series.data)) {
          return series;
        }
        const rawData = series.data;
        // Time series: [["2026-01-01T00:00:00+00:00", 24772], ...]
        const isTimeSeries = rawData.length > 0 &&
          Array.isArray(rawData[0]) &&
          rawData[0].length >= 2 &&
          (typeof rawData[0][0] === 'string' && (String(rawData[0][0]).includes('-') || String(rawData[0][0]).includes('T')) || typeof rawData[0][0] === 'number');

        const normalizedData = rawData
          .filter((item: any) => {
            if (Array.isArray(item) && item.length >= 2) {
              return item[1] !== null && item[1] !== undefined;
            }
            if (typeof item === 'object' && item !== null) {
              return (item.y != null) || (item.value != null);
            }
            return item !== null && item !== undefined;
          })
          .map((item: any) => {
            // Time series: convert date string to milliseconds for Highcharts datetime axis (line/area)
            if (isTimeSeries && Array.isArray(item) && item.length >= 2) {
              const timestamp = item[0];
              const value = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] ?? 0);
              if (typeof timestamp === 'string') {
                const date = new Date(timestamp);
                return isNaN(date.getTime()) ? [timestamp, value] : [date.getTime(), value];
              }
              return [typeof timestamp === 'number' ? timestamp : parseFloat(timestamp) || 0, value];
            }
            // Handle simple numeric values
            if (typeof item === 'string' && !isNaN(parseFloat(item)) && isFinite(parseFloat(item))) {
              return parseFloat(item);
            }
            if (typeof item === 'number') {
              return item;
            }
            // Handle objects with y or value properties
            if (typeof item === 'object' && item !== null) {
              const normalizedItem: any = { ...item };
              if (item.y !== undefined) {
                normalizedItem.y = typeof item.y === 'string' ? parseFloat(item.y) || 0 : item.y;
              }
              if (item.value !== undefined) {
                normalizedItem.value = typeof item.value === 'string' ? parseFloat(item.value) || 0 : item.value;
              }
              if (normalizedItem.name === null || normalizedItem.name === undefined) {
                normalizedItem.name = 'Unknown';
              }
              return normalizedItem;
            }
            // Handle arrays (pie: [name, value])
            if (Array.isArray(item) && item.length >= 2) {
              const [name, value] = item;
              const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (value ?? 0);
              const normalizedName = (name === null || name === undefined || name === '') ? 'Unknown' : name;
              return [normalizedName, numValue];
            }
            return item;
          });

        return { ...series, data: normalizedData };
      });
    }

    return normalized;
  }

  normalizeChartConfig(config: any): any {
    if (!config || typeof config !== 'object') {
      return config;
    }

    const normalized: any = {
      ...config
    };

    // Normalize config.series data - convert string numeric values to numbers
    if (Array.isArray(config.series)) {
      normalized.series = config.series.map((series: any) => {
        if (series && Array.isArray(series.data)) {
          return {
            ...series,
            data: series.data.map((item: any) => {
              // Handle simple numeric values (strings that represent numbers)
              if (typeof item === 'string' && !isNaN(parseFloat(item)) && isFinite(parseFloat(item))) {
                return parseFloat(item);
              }
              // Handle simple numeric values (already numbers)
              if (typeof item === 'number') {
                return item;
              }
              // Handle objects with y or value properties
              if (typeof item === 'object' && item !== null) {
                const normalizedItem: any = { ...item };
                // Convert y value from string to number if it's a string
                if (item.y !== undefined) {
                  normalizedItem.y = typeof item.y === 'string' ? parseFloat(item.y) || 0 : item.y;
                }
                // Convert value from string to number if it's a string
                if (item.value !== undefined) {
                  normalizedItem.value = typeof item.value === 'string' ? parseFloat(item.value) || 0 : item.value;
                }
                // Handle null / empty names (legend, tooltip, axis labels)
                if (
                  normalizedItem.name === null ||
                  normalizedItem.name === undefined ||
                  normalizedItem.name === ''
                ) {
                  normalizedItem.name = 'Unknown';
                }
                return normalizedItem;
              }
              // Handle arrays (for pie charts: [name, value])
              if (Array.isArray(item) && item.length >= 2) {
                const [name, value] = item;
                const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
                const normalizedName = (name === null || name === undefined || name === '') ? 'Unknown' : name;
                return [normalizedName, numValue];
              }
              return item;
            })
          };
        }
        return series;
      });
    }

    return normalizeHighchartsApiConfig(normalized, normalized.chart?.type);
  }

  handleDeleteDashboard() {
    this.showMoreDropdown = false;

    if (!this.dashboardId) {
      this.confirmPopupService.openConfirmDialog({
        title: 'Error',
        message: 'Dashboard ID missing',
        confirmText: 'OK',
        cancelText: '',
        showCancelButton: false
      }).subscribe();
      return;
    }

    // Check if current user is the dashboard creator
    if (!this.isDashboardCreator()) {
      // Removed delete popup for non-creators
      return;
    }

    this.confirmPopupService.openConfirmDialog({
      title: 'Delete Dashboard',
      message: `Are you sure you want to delete "${this.dashboardName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'delete-button'
    }).subscribe((result) => {
      if (result === true) {
        this.userDashboardService.deleteDashboard(this.dashboardId!).subscribe({
          next: () => {
            // Removed delete success popup
            this.router.navigate(['/user-dashboard']);
          },
          error: (error: any) => {
            console.error('Error deleting dashboard:', error);
            // Handle 403 Forbidden error
            if (error?.status === 403) {
              this.notificationService.info('Access Denied', 'Insufficient permissions');
            } else {
              this.notificationService.error('Error', 'Failed to delete dashboard');
            }
          }
        });
      }
    });
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    // If no unsaved changes, allow navigation
    if (!this.hasUnsavedChanges) {
      return true;
    }

    // If there are unsaved changes, show confirmation dialog
    return firstValueFrom(this.confirmPopupService.openConfirmDialog({
      title: 'Unsaved Changes',
      message: 'You have unsaved changes to your dashboard. Are you sure you want to leave without saving?',
      confirmText: 'Leave',
      cancelText: 'Stay',
      confirmButtonClass: 'leave-button'
    })).then((result: boolean) => {
      return result === true;
    });
  }

  // Method to check for unsaved changes
  private checkForUnsavedChanges() {
    // Always check for unsaved changes when in edit mode, regardless of whether it's new or existing dashboard
    if (!this._isEditMode) {
      this.hasUnsavedChanges = false;
      return;
    }

    // For new dashboards, check if there's any content
    if (!this.dashboardId) {
      this.hasUnsavedChanges = this.charts.length > 0 ||
                              (this.dashboardName.trim() !== '' && this.dashboardName !== 'Untitled Dashboard');
      return;
    }

    // For existing dashboards in edit mode, compare with original data
    if (this.originalDashboardData) {
      const currentData = {
        dashboardName: this.dashboardName,
        charts: this.charts,
        layout: this.selectedLayout
      };

      this.hasUnsavedChanges = !this.deepEqual(currentData, this.originalDashboardData);
    } else {
      // If we don't have original data, assume there are changes if we're in edit mode
      this.hasUnsavedChanges = true;
    }
  }

  // Helper method for deep comparison
  private deepEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;
    if (typeof obj1 !== typeof obj2) return false;

    if (typeof obj1 !== 'object') return obj1 === obj2;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (let key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!this.deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  }

  // Store original data when dashboard is loaded
  private storeOriginalData() {
    this.originalDashboardData = {
      dashboardName: this.dashboardName,
      charts: [...this.charts],
      layout: this.selectedLayout
    };
  }
}
